import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FaUser,
  FaEnvelope,
  FaBuilding,
  FaIdCard,
  FaPhone,
  FaBriefcase,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUserFriends,
  FaGraduationCap,
  FaClock,
  FaUserCircle
} from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './Profile.css';

const Profile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const baseUrl = "https://api.burrayhr.com";

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseUrl}/api/profile/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setProfileData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch profile information',
        confirmButtonColor: '#1e3a8a'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div>
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading profile information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Topbar />
      <Sidebar />
      <div className="main-content">
        <div className="profile-container">
          <div className="profile-header">
            <div className="profile-photo-section">
              {profileData?.employee_data?.photo ? (
                <img 
                  src={profileData.employee_data.photo} 
                  alt="Profile" 
                  className="profile-photo"
                />
              ) : (
                <FaUserCircle className="default-photo" />
              )}
            </div>
            <div className="profile-title">
              <h1>{profileData?.first_name} {profileData?.last_name}</h1>
              <p className="role">{profileData?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
              <p className="company">{profileData?.company_name}</p>
            </div>
          </div>

          <div className="profile-content">
            <div className="info-section">
              <h2>Basic Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <FaEnvelope className="info-icon" />
                  <div className="info-text">
                    <label>Email</label>
                    <p>{profileData?.email}</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaIdCard className="info-icon" />
                  <div className="info-text">
                    <label>Employee ID</label>
                    <p>{profileData?.employee_data?.employee_id || 'Not assigned'}</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaPhone className="info-icon" />
                  <div className="info-text">
                    <label>Phone Number</label>
                    <p>{profileData?.employee_data?.phone_number || 'Not provided'}</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaBriefcase className="info-icon" />
                  <div className="info-text">
                    <label>Position</label>
                    <p>{profileData?.employee_data?.position || 'Not assigned'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h2>Employment Details</h2>
              <div className="info-grid">
                <div className="info-item">
                  <FaBuilding className="info-icon" />
                  <div className="info-text">
                    <label>Department</label>
                    <p>{profileData?.employee_data?.department || 'Not assigned'}</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaBriefcase className="info-icon" />
                  <div className="info-text">
                    <label>Category</label>
                    <p className="capitalize">{profileData?.employee_data?.category || 'Not specified'}</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaClock className="info-icon" />
                  <div className="info-text">
                    <label>Length of Service</label>
                    <p>{profileData?.employee_data?.length_of_service || 'Not available'}</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaUser className="info-icon" />
                  <div className="info-text">
                    <label>Employment Status</label>
                    <p className="capitalize">{profileData?.employee_data?.employment_status || 'Not specified'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h2>Personal Information</h2>
              <div className="info-grid">
                <div className="info-item">
                  <FaCalendarAlt className="info-icon" />
                  <div className="info-text">
                    <label>Date of Birth</label>
                    <p>{formatDate(profileData?.employee_data?.date_of_birth)}</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaUser className="info-icon" />
                  <div className="info-text">
                    <label>Gender</label>
                    <p className="capitalize">{profileData?.employee_data?.gender || 'Not provided'}</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaMapMarkerAlt className="info-icon" />
                  <div className="info-text">
                    <label>Home Address</label>
                    <p>{profileData?.employee_data?.home_address || 'Not provided'}</p>
                  </div>
                </div>
                <div className="info-item">
                  <FaGraduationCap className="info-icon" />
                  <div className="info-text">
                    <label>Highest Qualification</label>
                    <p>{profileData?.employee_data?.highest_qualification || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </div>

            {(profileData?.employee_data?.spouse_name || profileData?.employee_data?.spouse_contact) && (
              <div className="info-section">
                <h2>Spouse Information</h2>
                <div className="info-grid">
                  <div className="info-item">
                    <FaUserFriends className="info-icon" />
                    <div className="info-text">
                      <label>Spouse Name</label>
                      <p>{profileData?.employee_data?.spouse_name || 'Not provided'}</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaPhone className="info-icon" />
                    <div className="info-text">
                      <label>Spouse Contact</label>
                      <p>{profileData?.employee_data?.spouse_contact || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;